import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesCampaignImportItem } from "../../../Models/voicesCampaigns";
import {
  useImportVoicesCampaigns,
  usePreviewVoicesCampaignImport,
} from "../../../Query/useVoicesCampaigns";
import { ComoButton } from "../../Common/ComoButton";
import { VoicesCampaignFormat } from "./voicesCampaignFormat";

interface Props {
  open: boolean;
  onClose: () => void;
}

const VoicesCampaignImportDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation("voicesCampaigns");
  const input = useRef<HTMLInputElement>(null);
  const preview = usePreviewVoicesCampaignImport();
  const importCampaigns = useImportVoicesCampaigns();
  const [content, setContent] = useState<unknown>(null);
  const [fileName, setFileName] = useState("");
  const [notJson, setNotJson] = useState(false);

  const result = content ? preview.data : undefined;
  const ready = result?.valid ? result.campaigns.length : 0;

  const close = () => {
    setContent(null);
    setFileName("");
    setNotJson(false);
    preview.reset();
    onClose();
  };

  const read = async (file: File) => {
    setFileName(file.name);
    setContent(null);
    preview.reset();
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setNotJson(true);
      return;
    }
    setNotJson(false);
    setContent(parsed);
    preview.mutate(parsed);
  };

  const details = (item: VoicesCampaignImportItem) =>
    [
      item.starts_on || item.ends_on
        ? `${VoicesCampaignFormat.date(item.starts_on ?? null)} – ${VoicesCampaignFormat.date(item.ends_on ?? null)}`
        : t("noPeriod"),
      t("importTerms", { count: item.search_terms?.length ?? 0 }),
      t("importFeeds", { count: item.feeds ?? 0 }),
    ].join(" · ");

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>{t("importTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: color.gray600, mb: 2 }}>
          {t("importHint")}
        </Typography>

        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) read(file);
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => input.current?.click()}
            disabled={preview.isPending || importCampaigns.isPending}
            sx={{
              textTransform: "none",
              color: color.gray900,
              borderColor: color.gray300,
              "&:hover": { borderColor: color.gray400, backgroundColor: color.gray50 },
            }}
          >
            {fileName ? t("importChooseAnother") : t("importChoose")}
          </Button>
          {fileName && (
            <Typography variant="body2" sx={{ color: color.gray600 }}>
              {fileName}
            </Typography>
          )}
        </Box>

        {notJson && (
          <Typography variant="body2" sx={{ color: color.red700, mt: 2 }}>
            {t("importNotJson")}
          </Typography>
        )}
        {preview.isPending && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, color: color.gray600 }}>
            <CircularProgress size={16} sx={{ color: "inherit" }} />
            <Typography variant="body2">{t("importReading")}</Typography>
          </Box>
        )}

        {result && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: result.valid ? color.gray900 : color.red700, mb: 1 }}
            >
              {result.valid ? t("importReady", { count: ready }) : t("importProblems")}
            </Typography>
            <Box sx={{ border: `1px solid ${color.gray200}`, borderRadius: "10px" }}>
              {result.campaigns.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                    px: 1.5,
                    py: 1.25,
                    borderTop: index ? `1px solid ${color.gray100}` : "none",
                  }}
                >
                  {item.error ? (
                    <ErrorOutlineIcon sx={{ fontSize: 18, color: color.red700, mt: 0.25 }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: color.green700, mt: 0.25 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: color.gray900 }}>
                      {item.name || t("importUnnamed")}
                    </Typography>
                    {item.error ? (
                      <Typography variant="caption" sx={{ color: color.red700 }}>
                        {item.error}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ display: "block", color: color.gray600 }}>
                          {details(item)}
                        </Typography>
                        {item.renamed && (
                          <Typography variant="caption" sx={{ display: "block", color: color.gray500 }}>
                            {t("importRenamed")}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <ComoButton
          disabled={!ready || importCampaigns.isPending}
          onClick={() => importCampaigns.mutate(content, { onSuccess: close })}
        >
          {ready ? t("importConfirm", { count: ready }) : t("import")}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesCampaignImportDialog;
